import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Search,
  Add,
  Edit,
  Delete,
  Visibility,
  LocalHospital,
  Bed,
  Person,
  CalendarToday,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { listIPDAdmissions, deleteIPDAdmission } from "../../../../api/ipd";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import AdmissionsTableSkeleton from "../../../../Components/GeneralComponents/admissionsTableSkeleton/AdmissionsTableSkeleton";

const AdmissionsList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch admissions
  const {
    data: admissionsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["ipd-admissions", page, rowsPerPage, debouncedSearch, statusFilter],
    queryFn: () =>
      listIPDAdmissions(
        {
          page,
          page_size: rowsPerPage,
          search: debouncedSearch || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
        },
        token
      ),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => deleteIPDAdmission(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["ipd-admissions"]);
      setDeleteDialogOpen(false);
      setSelectedAdmission(null);
    },
  });

  const handleDelete = (admission) => {
    setSelectedAdmission(admission);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedAdmission) {
      deleteMutation.mutate(selectedAdmission.id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success";
      case 2:
        return "info";
      case 3:
        return "warning";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return t("ipd.admissions.status.admitted");
      case 2:
        return t("ipd.admissions.status.discharged");
      case 3:
        return t("ipd.admissions.status.transferred");
      case 4:
        return t("ipd.admissions.status.absent");
      default:
        return t("ipd.admissions.status.unknown");
    }
  };

  const getAdmissionTypeText = (type) => {
    switch (type) {
      case 1:
        return t("ipd.admissions.type.emergency");
      case 2:
        return t("ipd.admissions.type.elective");
      case 3:
        return t("ipd.admissions.type.transfer");
      case 4:
        return t("ipd.admissions.type.observation");
      default:
        return t("ipd.admissions.type.unknown");
    }
  };

  if (isLoading) {
    return <AdmissionsTableSkeleton columns={8} rows={10} />;
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t("ipd.admissions.error_loading", "Error loading admissions")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          {t("ipd.admissions.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/ipd/admissions/new")}
        >
          {t("ipd.admissions.add_admission")}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder={t("ipd.admissions.search_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">{t("ipd.admissions.status.all")}</MenuItem>
              <MenuItem value="1">
                {t("ipd.admissions.status.admitted")}
              </MenuItem>
              <MenuItem value="2">
                {t("ipd.admissions.status.discharged")}
              </MenuItem>
              <MenuItem value="3">
                {t("ipd.admissions.status.transferred")}
              </MenuItem>
              <MenuItem value="4">{t("ipd.admissions.status.absent")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Admissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("ipd.admissions.patient")}</TableCell>
              <TableCell>{t("ipd.admissions.bed")}</TableCell>
              <TableCell>{t("ipd.admissions.admission_date")}</TableCell>
              <TableCell>{t("ipd.admissions.type_label")}</TableCell>
              <TableCell>{t("ipd.admissions.status_label")}</TableCell>
              <TableCell>{t("ipd.admissions.doctor")}</TableCell>
              <TableCell>{t("ipd.admissions.diagnosis")}</TableCell>
              <TableCell>{t("ipd.admissions.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(admissionsData)
              ? admissionsData
              : admissionsData?.results || []
            ).map((admission) => (
              <TableRow key={admission.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Person color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {admission.patient_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {admission.patient_phone}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Bed color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {admission.bed_info}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {admission.ward_name}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday color="primary" />
                    <Typography variant="body2">
                      {new Date(admission.admission_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getAdmissionTypeText(admission.admission_type)}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(admission.status)}
                    size="small"
                    color={getStatusColor(admission.status)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {admission.admitting_doctor_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {admission.admission_diagnosis}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title={t("ipd.admissions.view")}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/ipd/admissions/${admission.id}`)
                        }
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("ipd.admissions.edit")}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/dashboard/ipd/admissions/${admission.id}/edit`
                          )
                        }
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("ipd.admissions.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(admission)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        flexWrap="wrap"
        gap={2}
      >
        {/* Rows Per Page Selector */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">
            {t("ipd.admissions.rows_per_page", "Rows Per Page")}:
          </Typography>
          <FormControl variant="standard" sx={{ minWidth: 80 }}>
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(1);
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Pagination Component */}
        {(Array.isArray(admissionsData)
          ? admissionsData.length
          : admissionsData?.count || 0) > rowsPerPage && (
          <Pagination
            count={Math.ceil(
              (Array.isArray(admissionsData)
                ? admissionsData.length
                : admissionsData?.count || 0) / rowsPerPage
            )}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("ipd.admissions.delete_confirm_title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("ipd.admissions.delete_confirm_message", {
              patient: selectedAdmission?.patient_name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t("common.delete")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdmissionsList;
